import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import LeadTable from "../components/leads/LeadTable";
import LeadKanban from "../components/leads/LeadKanban";
import LeadFilters from "../components/leads/LeadFilters";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import LeadForm from "../components/leads/LeadForm";
import { leadService } from "../services/leads";
import toast from "react-hot-toast";

const Leads = () => {
  const [view, setView] = useState("table");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const queryClient = useQueryClient();

  const deleteLead = useMutation({
    mutationFn: (id) => leadService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Lead deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error deleting lead");
    },
  });

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const editLead = (lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      deleteLead.mutate(id);
    }
  };

  const openCreateModal = () => {
    setSelectedLead(null);
    setIsModalOpen(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and track your leads</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={filters.search || ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-56"
            />
          </div>
          <Button onClick={() => setView("table")} variant={view === "table" ? "primary" : "ghost"} size="sm">Table</Button>
          <Button onClick={() => setView("kanban")} variant={view === "kanban" ? "primary" : "ghost"} size="sm">Kanban</Button>
          <Button onClick={openCreateModal}>+ Add Lead</Button>
        </div>
      </div>
      <LeadFilters onFilter={handleFilter} />
      {view === "table" ? (
        <LeadTable filters={filters} onEdit={editLead} onDelete={handleDelete} />
      ) : (
        <LeadKanban filters={filters} />
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedLead ? "Edit Lead" : "Add Lead"}
      >
        <LeadForm
          lead={selectedLead}
          onSuccess={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default Leads;
